import {
  useNavigation,
  useNavigationState,
} from '../../navigation/NavigationProvider';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { DialogContent } from '../../../components/ui/DialogContent';
import { DialogActions } from '../../../components/ui/DialogActions';
import { DialogHeader } from '../../../components/ui/DialogHeader';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  DialogContentText,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CollectionMemberSummary,
  CollectionRole,
  ListCollectionPeopleResult,
  hasCollectionCapability,
} from '@qkindex/contracts';
import {
  ChevronRightIcon,
  InfoIcon,
  UserPlusIcon,
} from '../../../components/ui/icons';
import { LoadingState, SectionEmptyState } from '../../../components/ui/States';
import { ResponsiveDialog } from '../../../components/ui/ResponsiveDialog';
import {
  collectionPeopleErrorMessage,
  createCollectionPeopleController,
} from '../collectionPeopleService';
import { getLocalInvitationDelivery } from '../../../firebase/functions';

const safeDisplayText = (value: string): string =>
  Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) || 0;
    return codePoint <= 0x1f ||
      (codePoint >= 0x7f && codePoint <= 0x9f) ||
      codePoint === 0x61c ||
      codePoint === 0x200e ||
      codePoint === 0x200f ||
      (codePoint >= 0x2028 && codePoint <= 0x202e) ||
      (codePoint >= 0x2066 && codePoint <= 0x2069)
      ? ' '
      : character;
  })
    .join('')
    .replace(/\s+/gu, ' ')
    .trim() || 'Member';

const initials = (value: string): string => {
  const words = safeDisplayText(value).split(/\s+/u);
  return (
    words.length > 1
      ? `${Array.from(words[0])[0]}${Array.from(words[words.length - 1])[0]}`
      : Array.from(words[0]).slice(0, 2).join('')
  ).toUpperCase();
};

export function CollectionPeopleSection({
  collectionId,
  actorRole,
  actorUserId,
  onRoles,
}: {
  collectionId: string;
  actorRole: CollectionRole;
  actorUserId: string;
  onRoles?: () => void;
}) {
  const canManageMembers = hasCollectionCapability(actorRole, 'manageMembers');
  const canManageAdministrators = hasCollectionCapability(
    actorRole,
    'manageAdministrators',
  );
  const canManageMember = (member: CollectionMemberSummary) =>
    canManageMembers &&
    member.status === 'active' &&
    member.userId !== actorUserId &&
    member.role !== 'owner' &&
    (member.role !== 'administrator' || canManageAdministrators);
  const controller = useMemo(() => createCollectionPeopleController(), []);
  const [result, setResult] = useState<ListCollectionPeopleResult>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [infoMessage, setInfoMessage] = useState<string>();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'administrator' | 'editor' | 'viewer'>(
    'viewer',
  );
  const panelKey = `collection.peoplePanel.${collectionId}.${actorUserId}`;
  const [panel, setPanel] = useNavigationState<
    { kind: 'invite' | 'member' | 'invitation'; id?: string } | undefined
  >(panelKey, undefined);
  const [roleOverride, setRoleOverride] = useState<
    'administrator' | 'editor' | 'viewer'
  >();
  const selectedMember = result?.members.items.find(
    (member) =>
      panel?.kind === 'member' &&
      member.userId === panel.id &&
      canManageMember(member),
  );
  const draftRole =
    roleOverride ??
    (selectedMember?.role !== 'owner' ? selectedMember?.role : undefined) ??
    'viewer';
  const selectedInvitation = result?.invitations.items.find(
    (invitation) =>
      panel?.kind === 'invitation' &&
      invitation.invitationId === panel.id &&
      invitation.status === 'pending' &&
      (invitation.role !== 'administrator' || canManageAdministrators),
  );
  const panelGuard = useUnsavedChangesGuard({
    scopeKey: panelKey,
    open: Boolean(panel),
    dirty:
      panel?.kind === 'invite'
        ? Boolean(email.trim())
        : Boolean(selectedMember && draftRole !== selectedMember.role),
    pending: Boolean(busy),
    onDiscard: () => {
      setEmail('');
      setRoleOverride(undefined);
    },
    onClose: () => setPanel(undefined),
  });
  const closePanel = () => {
    void panelGuard.requestClose();
  };
  const finishPanel = () =>
    panelGuard.runAfterSave(() => {
      setRoleOverride(undefined);
      replaceState(panelKey, undefined);
    });
  const scopeKey = `collection.removeMember.${collectionId}.${actorUserId}`;
  const { replaceState } = useNavigation();
  const [removeUserId, setRemoveUserId] = useNavigationState<
    string | undefined
  >(scopeKey, undefined);
  const memberToRemove = result?.members.items.find(
    (member) => member.userId === removeUserId && canManageMember(member),
  );
  const { requestClose, runAfterSave } = useUnsavedChangesGuard({
    scopeKey,
    open: Boolean(memberToRemove) || Boolean(busy),
    dirty: false,
    pending: Boolean(busy),
    onDiscard: () => {},
    onClose: () => setRemoveUserId(undefined),
  });
  const dismissRemoval = () => {
    void requestClose();
  };
  useEffect(() => {
    if (!loading && !busy && removeUserId && !memberToRemove)
      replaceState(scopeKey, undefined);
  }, [loading, busy, removeUserId, memberToRemove, replaceState, scopeKey]);
  const activeCollection = useRef(collectionId);

  activeCollection.current = collectionId;

  const load = async (
    append = false,
    target: 'members' | 'invitations' | 'both' = 'both',
  ) => {
    const requestedCollectionId = collectionId;
    setLoading(true);
    setError(undefined);
    try {
      const page = await controller.list(
        collectionId,
        append
          ? {
              ...(target !== 'invitations'
                ? { memberCursor: result?.members.nextCursor }
                : {}),
              ...(target !== 'members'
                ? { invitationCursor: result?.invitations.nextCursor }
                : {}),
            }
          : {},
      );
      if (activeCollection.current !== requestedCollectionId) return;
      setResult((current) =>
        append && current
          ? {
              members:
                target === 'invitations'
                  ? current.members
                  : {
                      items: [...current.members.items, ...page.members.items],
                      nextCursor: page.members.nextCursor,
                    },
              invitations:
                target === 'members'
                  ? current.invitations
                  : {
                      items: [
                        ...current.invitations.items,
                        ...page.invitations.items,
                      ],
                      nextCursor: page.invitations.nextCursor,
                    },
            }
          : page,
      );
    } catch (loadError) {
      if (activeCollection.current !== requestedCollectionId) return;
      setError(collectionPeopleErrorMessage(loadError));
    } finally {
      if (activeCollection.current === requestedCollectionId) setLoading(false);
    }
  };

  useEffect(() => {
    controller.reset();
    setRole('viewer');
    setResult(undefined);
    setError(undefined);
    setLoading(true);
    void load();
    return () => controller.reset();
    // The controller is collection-bound and deliberately resets on switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, actorRole, actorUserId, controller]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !canManageMembers) return;
    setBusy('invite');
    setError(undefined);
    try {
      await controller.invite(collectionId, email, role);
      controller.acknowledgeInvite();
      setEmail('');
      finishPanel();
      await load();
    } catch (inviteError) {
      setError(collectionPeopleErrorMessage(inviteError));
    } finally {
      setBusy(undefined);
    }
  };

  const mutate = async (
    action: 'resend' | 'revoke',
    invitationId: string,
    revision: number,
  ) => {
    if (busy || !canManageMembers) return;
    setBusy(`${action}:${invitationId}`);
    setError(undefined);
    try {
      await controller[action](collectionId, invitationId, revision);
      controller.acknowledge(invitationId);
      if (action === 'revoke') finishPanel();
      await load();
    } catch (mutationError) {
      setError(collectionPeopleErrorMessage(mutationError));
    } finally {
      setBusy(undefined);
    }
  };

  const copyLocalLink = async (invitationId: string) => {
    if (busy || !canManageMembers) return;
    setBusy(`copy:${invitationId}`);
    setError(undefined);
    setInfoMessage(undefined);
    try {
      const res = await getLocalInvitationDelivery({
        collectionId,
        invitationId,
      });
      const rawToken = (res.data as { rawToken: string }).rawToken;
      const url = `${window.location.origin}/#invite=${rawToken}`;
      await navigator.clipboard.writeText(url);
      setInfoMessage(`Copied invitation link to clipboard: ${url}`);
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof err.message === 'string'
          ? err.message
          : 'Unable to fetch local invitation link.';
      setError(
        `${message} (If local development was restarted, click 'Resend' to generate a fresh link).`,
      );
    } finally {
      setBusy(undefined);
    }
  };

  const mutateMember = async (
    action: 'role' | 'remove',
    member: CollectionMemberSummary,
    nextRole?: 'administrator' | 'editor' | 'viewer',
  ) => {
    if (
      busy ||
      member.role === 'owner' ||
      !canManageMember(member) ||
      (nextRole === 'administrator' && !canManageAdministrators)
    )
      return;
    setBusy(`${action}:${member.userId}`);
    setError(undefined);
    try {
      if (action === 'role' && nextRole) {
        await controller.changeRole(
          collectionId,
          member.userId,
          member.revision,
          member.membershipVersion,
          nextRole,
        );
      } else if (action === 'remove') {
        await controller.removeMember(
          collectionId,
          member.userId,
          member.revision,
          member.membershipVersion,
          member.role,
        );
      }
      controller.acknowledgeMember(member.userId);
      if (action === 'remove')
        runAfterSave(() => replaceState(scopeKey, undefined));
      finishPanel();
      await load();
    } catch (mutationError) {
      setError(collectionPeopleErrorMessage(mutationError));
    } finally {
      setBusy(undefined);
    }
  };

  if (!canManageMembers) return null;

  const roleLabel = (value: CollectionRole) =>
    value.charAt(0).toUpperCase() + value.slice(1);
  const name = (member: CollectionMemberSummary) =>
    member.userId === actorUserId ? 'You' : safeDisplayText(member.displayName);
  const roleOptions = [
    <MenuItem key="viewer" value="viewer">
      Viewer
    </MenuItem>,
    <MenuItem key="editor" value="editor">
      Editor
    </MenuItem>,
    ...(canManageAdministrators
      ? [
          <MenuItem key="administrator" value="administrator">
            Administrator
          </MenuItem>,
        ]
      : []),
  ];
  const pendingInvitations =
    result?.invitations.items.filter(
      (invitation) => invitation.status === 'pending',
    ) || [];
  const rowContent = (
    title: string,
    subtitle: string,
    editable: boolean,
    avatarName = title,
  ) => (
    <>
      <Avatar
        sx={{
          width: 34,
          height: 34,
          fontSize: 12,
          bgcolor: 'action.hover',
          color: 'primary.main',
          mr: 2,
        }}
      >
        {initials(avatarName)}
      </Avatar>
      <ListItemText
        sx={{ minWidth: 0, m: 0 }}
        primary={
          <Box component="bdi" dir="auto">
            {title}
          </Box>
        }
        secondary={subtitle}
        primaryTypographyProps={{
          variant: 'body2',
          fontWeight: 600,
          color: 'primary.main',
          sx: { overflowWrap: 'anywhere' },
        }}
        secondaryTypographyProps={{
          variant: 'body2',
          mt: 0.25,
          sx: { overflowWrap: 'anywhere' },
        }}
      />
      {editable && (
        <ChevronRightIcon
          aria-hidden="true"
          sx={{ ml: 1, color: 'text.secondary' }}
        />
      )}
    </>
  );
  return (
    <Stack spacing={2}>
      <Button
        startIcon={<InfoIcon />}
        onClick={onRoles}
        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
      >
        <Box component="span" sx={{ flex: 1 }}>
          Roles and permissions
        </Box>
        <ChevronRightIcon aria-hidden="true" />
      </Button>
      <Divider />
      {error && (
        <Alert severity="error">
          {error}
          {!result && (
            <Button
              onClick={() => {
                void load();
              }}
            >
              Try again
            </Button>
          )}
        </Alert>
      )}
      {infoMessage && <Alert severity="info">{infoMessage}</Alert>}
      {loading && !result ? (
        <LoadingState label="Loading people…" />
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography component="h3" variant="body2" color="text.secondary">
              Members
            </Typography>
            <Button
              startIcon={<UserPlusIcon />}
              disabled={Boolean(busy)}
              onClick={() => {
                setEmail('');
                setRole('viewer');
                setPanel({ kind: 'invite' });
              }}
            >
              Invite
            </Button>
          </Stack>
          <List disablePadding aria-label="Collection members">
            {result?.members.items.map((member) => (
              <ListItem key={member.userId} disablePadding>
                {canManageMember(member) ? (
                  <ListItemButton
                    disabled={Boolean(busy)}
                    sx={{ borderRadius: 2, px: 1, py: 1.5 }}
                    onClick={() => {
                      setRoleOverride(undefined);
                      setPanel({ kind: 'member', id: member.userId });
                    }}
                  >
                    {rowContent(
                      name(member),
                      roleLabel(member.role),
                      true,
                      member.displayName,
                    )}
                  </ListItemButton>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      px: 1,
                      py: 1.5,
                    }}
                  >
                    {rowContent(
                      name(member),
                      `${roleLabel(member.role)}${member.status !== 'active' ? ` · ${member.status}` : ''}`,
                      false,
                      member.displayName,
                    )}
                  </Box>
                )}
              </ListItem>
            ))}
          </List>
          {result?.members.nextCursor && (
            <Button
              disabled={loading}
              onClick={() => {
                void load(true, 'members');
              }}
            >
              {loading ? 'Loading…' : 'Load more members'}
            </Button>
          )}
          {(pendingInvitations.length > 0 ||
            result?.invitations.nextCursor) && (
            <>
              <Divider />
              <Typography component="h3" variant="body2" color="text.secondary">
                Pending invitations
              </Typography>
              <List disablePadding aria-label="Collection invitations">
                {pendingInvitations.map((invitation) => {
                  const editable =
                    invitation.status === 'pending' &&
                    (invitation.role !== 'administrator' ||
                      canManageAdministrators);
                  const content = rowContent(
                    invitation.email,
                    `${roleLabel(invitation.role)} · ${invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}`,
                    editable,
                  );
                  return (
                    <ListItem key={invitation.invitationId} disablePadding>
                      {editable ? (
                        <ListItemButton
                          disabled={Boolean(busy)}
                          sx={{ borderRadius: 2, px: 1, py: 1.5 }}
                          onClick={() =>
                            setPanel({
                              kind: 'invitation',
                              id: invitation.invitationId,
                            })
                          }
                        >
                          {content}
                        </ListItemButton>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            px: 1,
                            py: 1.5,
                          }}
                        >
                          {content}
                        </Box>
                      )}
                    </ListItem>
                  );
                })}
              </List>
              {result && pendingInvitations.length === 0 && (
                <SectionEmptyState
                  compact
                  icon={UserPlusIcon}
                  message={
                    result.invitations.nextCursor
                      ? 'No pending invitations on this page.'
                      : 'No pending invitations.'
                  }
                />
              )}
              {result?.invitations.nextCursor && (
                <Button
                  disabled={loading}
                  onClick={() => {
                    void load(true, 'invitations');
                  }}
                >
                  {loading ? 'Loading…' : 'Load more invitations'}
                </Button>
              )}
            </>
          )}
        </>
      )}
      <ResponsiveDialog
        open={
          panel?.kind === 'invite' ||
          Boolean(selectedMember) ||
          Boolean(selectedInvitation)
        }
        onClose={closePanel}
        aria-labelledby="people-panel-title"
      >
        <DialogHeader
          id="people-panel-title"
          title={
            panel?.kind === 'invite'
              ? 'Invite person'
              : selectedMember
                ? name(selectedMember)
                : 'Invitation'
          }
          onClose={closePanel}
          onBack={closePanel}
        />
        <DialogContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {infoMessage && <Alert severity="info">{infoMessage}</Alert>}
            {panel?.kind === 'invite' && (
              <Box
                component="form"
                id="people-invite-form"
                onSubmit={(event) => {
                  void submit(event);
                }}
              >
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email address"
                    value={email}
                    inputProps={{ maxLength: 320, autoComplete: 'email' }}
                    disabled={Boolean(busy)}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="invite-role-label">Role</InputLabel>
                    <Select
                      labelId="invite-role-label"
                      label="Role"
                      value={role}
                      disabled={Boolean(busy)}
                      onChange={(event) =>
                        setRole(event.target.value as typeof role)
                      }
                    >
                      {roleOptions}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
            )}
            {selectedMember && (
              <>
                <FormControl fullWidth>
                  <InputLabel id="member-role-label">Role</InputLabel>
                  <Select
                    labelId="member-role-label"
                    label="Role"
                    inputProps={{
                      'aria-label': `Role for ${safeDisplayText(selectedMember.displayName)}`,
                    }}
                    value={draftRole}
                    disabled={Boolean(busy)}
                    onChange={(event) =>
                      setRoleOverride(event.target.value as typeof draftRole)
                    }
                  >
                    {roleOptions}
                  </Select>
                </FormControl>
                <Button
                  color="error"
                  disabled={Boolean(busy)}
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setRemoveUserId(selectedMember.userId)}
                >
                  Remove
                </Button>
              </>
            )}
            {selectedInvitation && (
              <>
                <Typography>
                  <bdi dir="ltr">{selectedInvitation.email}</bdi>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {roleLabel(selectedInvitation.role)} · Pending
                </Typography>
                {(window.location.hostname === 'localhost' ||
                  window.location.hostname === '127.0.0.1') && (
                  <Button
                    disabled={Boolean(busy)}
                    onClick={() => {
                      void copyLocalLink(selectedInvitation.invitationId);
                    }}
                  >
                    Copy Link
                  </Button>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {selectedInvitation ? (
            <>
              <Button
                color="error"
                disabled={Boolean(busy)}
                onClick={() => {
                  void mutate(
                    'revoke',
                    selectedInvitation.invitationId,
                    selectedInvitation.revision,
                  );
                }}
              >
                Revoke
              </Button>
              <Button
                variant="contained"
                disabled={Boolean(busy)}
                onClick={() => {
                  void mutate(
                    'resend',
                    selectedInvitation.invitationId,
                    selectedInvitation.revision,
                  );
                }}
              >
                Resend
              </Button>
            </>
          ) : (
            <>
              <Button disabled={Boolean(busy)} onClick={closePanel}>
                Cancel
              </Button>
              {panel?.kind === 'invite' && (
                <Button
                  type="submit"
                  form="people-invite-form"
                  variant="contained"
                  disabled={Boolean(busy) || !email.trim()}
                >
                  {busy === 'invite' ? 'Inviting…' : 'Invite person'}
                </Button>
              )}
              {selectedMember && (
                <Button
                  variant="contained"
                  disabled={Boolean(busy) || draftRole === selectedMember.role}
                  onClick={() => {
                    void mutateMember('role', selectedMember, draftRole);
                  }}
                >
                  Save changes
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </ResponsiveDialog>
      <ResponsiveDialog
        open={Boolean(memberToRemove)}
        onClose={dismissRemoval}
        aria-labelledby="remove-member-title"
      >
        <DialogHeader id="remove-member-title" title="Remove member?" />
        <DialogContent>
          <DialogContentText>
            Their collection access and queued work will be revoked immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={Boolean(busy)} onClick={dismissRemoval}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={Boolean(busy) || !memberToRemove}
            onClick={() => {
              if (memberToRemove) void mutateMember('remove', memberToRemove);
            }}
          >
            Remove member
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Stack>
  );
}
